import { useState, useEffect } from 'react';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
} from 'recharts';
import {
  Box,
  Typography,
  Chip,
  Paper,
  CircularProgress
} from '@mui/material';
import ErrorHandler from '@Root/ErrorHandler';
import LabelViewExtendService from '@Helpers/URLHelpers/WeboutService/views/LabelViewExtendService';
import './LabelViewStatusOverView.scss';

export default function LabelViewStatusOverView(props) {
  const className = 'labelViewStatusOverView';
  const [, setLabelAnalyticsData] = useState({});
  const [showLoader, setShowLoader] = useState(true);
  const [data, setData] = useState([]);

  const defaultFilters = {
    labelViewFilter: {
      labelId: props?.labelData?.id,
      registryType: props?.labelData?.registryType,
      statusList: ['done', 'inProgress', 'backlog'],
    },
  };
  const [filters] = useState(defaultFilters);

  const labelViewExtendService = new LabelViewExtendService();
  const errorHandler = new ErrorHandler();

  useEffect(() => {
    renderData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.labelData?.id, props?.labelData?.registryType]);

  const renderData = async () => {
    try {
      setShowLoader(true);
      const response = await labelViewExtendService.getStatusCountByRegistryTypeAndStatus(filters);
      const responseJson = JSON.parse(response.body);
      setLabelAnalyticsData(responseJson);
      const chartDataObj = [
        { name: 'Done', value: responseJson?.done || 0 },
        { name: 'In Progress', value: responseJson?.inProgress || 0 },
        { name: 'Backlog', value: responseJson?.backlog || 0 },
      ];
      setData(chartDataObj);
    } catch (err) {
      errorHandler.handleError(err);
    }
    setShowLoader(false);
  };

  const getBarColor = (statusName) => {
    switch (statusName) {
      case 'Done':
        return 'var(--color-primaryBlue)';
      case 'In Progress':
        return 'var(--color-primaryGreen)';
      case 'Backlog':
        return 'var(--color-primaryGrey)';
      default:
        return 'var(--color-disabled)';
    }
  };

  const total = data.reduce((acc, cur) => acc + cur.value, 0);

  return (
    <Paper className={className + ' height-wfa'} elevation={0}>
      <Box className={`${className}__header`}>
        <Typography variant="subtitle1" className={`${className}__title`}>
          Status Overview
        </Typography>
        <Box className={`${className}__meta`}>
          <Chip
            size="small"
            label={`Total: ${total}`}
            variant="outlined"
            className={`${className}__chip`}
            color="primary"
          />
        </Box>
      </Box>

      <Box className={`${className}__body`}>
        {showLoader ? (
          <Box className={`${className}__loader`}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="textSecondary">
              Loading status counts...
            </Typography>
          </Box>
        ) : total === 0 ? (
          <Box className={`${className}__empty`}>
            <Typography variant="body2" className={`${className}__empty__text`}>
              No items for selected label
            </Typography>
          </Box>
        ) : (
          <Box className={`${className}__chart height-wfa`}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={data}
                layout="vertical"
                margin={{ top: 10, right: 24, left: 40, bottom: 10 }}
              >
                <XAxis type="number" tick={{ fontSize: 12, fill: 'var(--color-primaryGrey)' }} axisLine={false} tickLine={false} />
                <YAxis
                  dataKey="name"
                  type="category"
                  tick={{ fontSize: 12, fontWeight: 600, fill: 'var(--color-text)' }}
                  axisLine={false}
                  tickLine={false}
                  width={90}
                />
                <Tooltip
                  cursor={{ fill: 'var(--color-hoverBackground)' }}
                  contentStyle={{
                    borderRadius: 8,
                    border: '1px solid var(--color-darkBorder)',
                    background: 'var(--color-componentBackground)',
                    color: 'var(--color-text)',
                    padding: '0.5rem 0.75rem',
                    fontSize: 12
                  }}
                  formatter={(v, n) => [`${v}`, n]}
                />
                <Bar
                  dataKey="value"
                  barSize={26}
                  radius={[4, 4, 4, 4]}
                  animationDuration={600}
                >
                  {data.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={getBarColor(entry.name)}
                      style={{ filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.15))' }}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
